import {
  Component,
  effect,
  inject,
  NgZone,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet, Scroll } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { trackKeyPageView } from './core/ads-conversions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet],
})
export class AppComponent {
  private router = inject(Router);
  private zone = inject(NgZone);

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(event => trackKeyPageView(event.urlAfterRedirects));

    const anchor: Signal<string | undefined> = toSignal(
      this.router.events.pipe(
        filter((event): event is Scroll => event instanceof Scroll),
        // Reload and back/forward restore the saved scroll position instead
        // (DefaultLayoutComponent), so only links jump to their #anchor.
        filter(() => {
          const nav = this.router.lastSuccessfulNavigation();
          return !!nav?.previousNavigation && nav.trigger !== 'popstate';
        }),
        map((event: Scroll) => event.anchor ?? undefined)
      )
    );

    effect(() => {
      if (anchor()) {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => {
            const el = document.getElementById(anchor()!);
            if (el && (!this.inViewport(el) || anchor() === 'progress-card')) {
              const scrollArea = document.getElementById('scroll-area');
              scrollArea?.scrollTo({
                top: Math.ceil(
                  scrollArea.scrollTop + el.getBoundingClientRect().top - 136
                ),
                behavior: 'instant',
              });
            }
          }, 900);
        });
      }
    });
  }

  private inViewport(el: HTMLElement | null): boolean {
    if (el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    }
    return false;
  }
}
